import React from 'react';
import PropTypes from 'prop-types';
import { Card, Badge, ChevronRight, ProgressBar } from '@opendate-app/setlist';
import { formatPrice } from '../../../shared/teamCurrencyFormatting';

const pluralize = (count, word) => `${count} ${count === 1 ? word : `${word}s`}`;

const CardShell = ({ label, badge, badgeColor, children, ctaLabel, href }) => (
  <Card padding="md" style={{ height: '100%' }}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minHeight: 150 }}>
      <div className="d-flex justify-content-between align-items-center" style={{ gap: 8 }}>
        <span className="figma-text sm semibold">{label}</span>
        <Badge size="sm" color={badgeColor}>{badge}</Badge>
      </div>
      <div>{children}</div>
      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ height: 1, background: 'var(--sl-border-secondary)' }} />
        <a href={href} className="figma-text sm semibold d-inline-flex align-items-center" style={{ gap: 4 }}>
          {ctaLabel}
          <ChevronRight width={16} height={16} />
        </a>
      </div>
    </div>
  </Card>
);

const Headline = ({ value, sub }) => (
  <>
    <div style={{ fontSize: 30, lineHeight: '38px', fontWeight: 700, color: 'var(--sl-text-primary)' }}>{value}</div>
    {sub && <div className="figma-text tertiary sm" style={{ marginTop: 2 }}>{sub}</div>}
  </>
);

const buildActionCards = ({ cards, teamId, currencyCode }) => {
  const fmt = (amount) => formatPrice(amount, currencyCode);
  const path = (name) => `/teams/${teamId}/${name}`;
  const built = [];

  if (cards.refunds) {
    const { count, amount, oldestDays, needsFinance } = cards.refunds;
    const base = oldestDays != null
      ? `${pluralize(count, 'request')} · oldest ${pluralize(oldestDays, 'day')}`
      : pluralize(count, 'request');
    const sub = needsFinance > 0 ? `${base} · ${needsFinance} need${needsFinance === 1 ? 's' : ''} Opendate finance` : base;
    built.push(
      <CardShell key="refunds" label="Refund requests" badge={`${count} pending`} badgeColor="warning"
        ctaLabel="Review requests" href={path('refund_requests')}>
        <Headline value={fmt(amount)} sub={sub} />
      </CardShell>
    );
  }
  if (cards.flagged) {
    const { count, amount } = cards.flagged;
    built.push(
      <CardShell key="flagged" label="Flagged orders" badge={`${count} flagged`} badgeColor="warning"
        ctaLabel="Review risk queue" href={path('payment_reviews')}>
        <Headline value={fmt(amount)} sub={pluralize(count, 'order')} />
      </CardShell>
    );
  }
  if (cards.chargebacks) {
    const { count, amount } = cards.chargebacks;
    built.push(
      <CardShell key="chargebacks" label="Chargebacks" badge={`${count} to respond`} badgeColor="error"
        ctaLabel="Submit evidence" href={path('payment_disputes')}>
        <Headline value={fmt(amount)} sub={pluralize(count, 'case')} />
      </CardShell>
    );
  }
  return built;
};

const LatestPayouts = ({ rows, currencyCode }) => (
  <div style={{ marginTop: 18 }}>
    <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 6 }}>
      <span className="figma-text sm semibold">Latest payouts</span>
    </div>
    {rows.map((row) => (
      <div key={row.id} className="d-flex align-items-center" style={{ gap: 16, padding: '10px 0', borderTop: '1px solid var(--sl-border-secondary)' }}>
        <span className="figma-text sm semibold" style={{ width: 110, flex: '0 0 auto', color: 'var(--sl-text-primary)' }}>
          {formatPrice(row.amount, currencyCode)}
        </span>
        <span className="figma-text sm" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.venue}
        </span>
        <span className="figma-text tertiary sm" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.events || '—'}
        </span>
        <span className="figma-text tertiary sm" style={{ width: 96, flex: '0 0 auto', textAlign: 'right' }}>{row.date}</span>
      </div>
    ))}
  </div>
);

const WeeklyPayoutChart = ({ weeks, currencyCode }) => {
  const max = Math.max(...weeks.map((week) => week.amount), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 96, marginTop: 18 }}>
      {weeks.map((week, index) => {
        const height = week.amount > 0 ? Math.max(6, Math.round((week.amount / max) * 84)) : 2;
        const isLast = index === weeks.length - 1;
        return (
          <div key={week.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div title={formatPrice(week.amount, currencyCode)}
              style={{ width: '100%', height, borderRadius: 4, background: isLast ? '#1982C4' : '#B2DBF5' }} />
            <span className="figma-text quaternary" style={{ fontSize: 11 }}>{week.label}</span>
          </div>
        );
      })}
    </div>
  );
};

const WinRateCard = ({ winRate }) => {
  const { pct, won, decided, benchmark } = winRate;
  const beatsAvg = benchmark != null && pct >= benchmark;
  return (
    <Card padding="lg">
      <div className="figma-text sm semibold">Dispute win rate</div>
      <div className="figma-text tertiary sm" style={{ marginTop: 2 }}>Won vs. lost, all time</div>
      <div style={{ fontSize: 36, lineHeight: '44px', fontWeight: 700, color: 'var(--sl-text-primary)', marginTop: 12 }}>
        {pct}%
      </div>
      <div className="figma-text tertiary sm" style={{ marginTop: 2 }}>{won} of {decided} won</div>
      {benchmark != null && (
        <div className="figma-text sm d-inline-flex align-items-center" style={{ gap: 6, marginTop: 12, color: beatsAvg ? '#6D9F1E' : '#B54708' }}>
          <span>{beatsAvg ? '▲' : '▼'} {Math.abs(pct - benchmark)} pts {beatsAvg ? 'above' : 'below'}</span>
          <span className="figma-text tertiary sm">the {benchmark}% Opendate average</span>
        </div>
      )}
    </Card>
  );
};

const formatDay = (isoDate) => isoDate
  && new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const PreviousRebates = ({ rebates, currencyCode }) => (
  // ponytail: native <details> over Setlist CollapsibleSection, whose summary text, hover and border ignore dark mode.
  <details>
    <summary className="figma-text sm semibold" style={{ cursor: 'pointer', padding: '6px 0', color: 'var(--sl-text-secondary)' }}>
      Previous rebates <span className="figma-text tertiary sm">({rebates.length})</span>
    </summary>
    {rebates.map((rebate) => (
      <div key={rebate.id} style={{ padding: '10px 0', borderTop: '1px solid var(--sl-border-secondary)' }}>
        <div className="d-flex justify-content-between align-items-center" style={{ gap: 8 }}>
          <span className="figma-text sm semibold" title={rebate.name} style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {rebate.name}
          </span>
          <Badge size="sm" color={rebate.fully_recouped ? 'success' : 'gray'}>
            {rebate.fully_recouped ? 'Fully recouped' : `Inactive · ${Math.floor(rebate.progress_percent)}% recouped`}
          </Badge>
        </div>
        <div className="figma-text tertiary sm" style={{ marginTop: 2 }}>
          {formatPrice(rebate.amount_collected, currencyCode)} recouped of {formatPrice(rebate.advance_amount, currencyCode)}
          {rebate.activated_on && ` · started ${formatDay(rebate.activated_on)}`}
        </div>
      </div>
    ))}
  </details>
);

const RebateCard = ({ rebate, previous, currencyCode }) => {
  const fmt = (amount) => formatPrice(amount, currencyCode);
  const started = rebate && formatDay(rebate.activated_on);
  return (
    <Card padding="lg">
      {rebate ? (
        <>
          <div className="d-flex justify-content-between align-items-center" style={{ gap: 8 }}>
            <span className="figma-text sm semibold">Rebate: {rebate.name}</span>
            <Badge size="sm" color={rebate.fully_recouped ? 'success' : 'brand'}>
              {rebate.fully_recouped ? 'Fully recouped' : 'Collecting'}
            </Badge>
          </div>
          <div style={{ fontSize: 36, lineHeight: '44px', fontWeight: 700, color: 'var(--sl-text-primary)', marginTop: 12 }}>
            {fmt(rebate.amount_collected)}
          </div>
          <div className="figma-text tertiary sm" style={{ marginTop: 2 }}>
            recouped of {fmt(rebate.advance_amount)} · {fmt(rebate.remaining)} remaining
          </div>
          <div style={{ marginTop: 12 }}>
            <ProgressBar value={rebate.progress_percent} tone={rebate.fully_recouped ? 'success' : 'brand'} label="right"
              format={(value) => `${Math.floor(value)}%`} aria-label="Rebate recouped" />
          </div>
          <div className="figma-text tertiary sm" style={{ marginTop: 12 }}>
            {rebate.per_ticket}{started && ` · started ${started}`}
          </div>
        </>
      ) : (
        <>
          <div className="figma-text sm semibold">Rebates</div>
          <div className="figma-text tertiary sm" style={{ marginTop: 2 }}>No active rebate</div>
        </>
      )}
      {previous.length > 0 && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--sl-border-secondary)', paddingTop: 8 }}>
          <PreviousRebates rebates={previous} currencyCode={currencyCode} />
        </div>
      )}
    </Card>
  );
};

const FinanceOverview = ({ team, currencyCode, cards = {}, payouts, rebate, previousRebates, winRate }) => {
  const showRebates = Boolean(rebate || previousRebates?.length);
  const actionCards = buildActionCards({ cards, teamId: team.id, currencyCode });
  const openItems = Object.values(cards).reduce((sum, card) => sum + (card.count || 0), 0);

  return (
    <div style={{ paddingBottom: 40 }}>
      {actionCards.length > 0 && (
        <>
          <div className="d-flex align-items-center" style={{ gap: 10, marginBottom: 12 }}>
            <h2 className="figma-text lg semibold" style={{ margin: 0 }}>Needs action</h2>
            <Badge size="sm" color={openItems > 0 ? 'error' : 'success'}>
              {openItems > 0 ? `${pluralize(openItems, 'open item')}` : 'All clear'}
            </Badge>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 20, marginBottom: 32 }}>
            {actionCards}
          </div>
        </>
      )}

      {(payouts || showRebates || winRate) && (
        <>
          <h2 className="figma-text lg semibold" style={{ margin: '0 0 12px' }}>Financial health</h2>
          <div style={{ display: 'grid', gridTemplateColumns: payouts && (showRebates || winRate) ? 'minmax(0, 2fr) minmax(0, 1fr)' : '1fr', gap: 20, alignItems: 'start' }}>
            {payouts && (
              <Card padding="lg">
                <div className="figma-text sm semibold">Recent payouts</div>
                <div className="figma-text tertiary sm" style={{ marginTop: 2 }}>Last 8 weeks</div>
                <div className="d-flex align-items-baseline" style={{ gap: 12, marginTop: 6 }}>
                  <div style={{ fontSize: 36, lineHeight: '44px', fontWeight: 700, color: 'var(--sl-text-primary)' }}>
                    {formatPrice(payouts.total, currencyCode)}
                  </div>
                  <div className="figma-text tertiary sm">across {pluralize(payouts.count, 'payout')}</div>
                </div>
                {payouts.weekly?.length > 0 && <WeeklyPayoutChart weeks={payouts.weekly} currencyCode={currencyCode} />}
                {payouts.recent.length > 0 && <LatestPayouts rows={payouts.recent} currencyCode={currencyCode} />}
                <a href={`/teams/${team.id}/payouts`} className="figma-text sm semibold d-inline-flex align-items-center" style={{ gap: 4, marginTop: 14 }}>
                  View all payouts
                  <ChevronRight width={16} height={16} />
                </a>
              </Card>
            )}
            {(showRebates || winRate) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
                {showRebates && <RebateCard rebate={rebate} previous={previousRebates || []} currencyCode={currencyCode} />}
                {winRate && <WinRateCard winRate={winRate} />}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

FinanceOverview.propTypes = {
  team: PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired,
  currencyCode: PropTypes.string.isRequired,
  cards: PropTypes.shape({
    refunds: PropTypes.shape({
      count: PropTypes.number.isRequired,
      amount: PropTypes.number.isRequired,
      oldestDays: PropTypes.number,
      needsFinance: PropTypes.number,
    }),
    flagged: PropTypes.shape({ count: PropTypes.number, amount: PropTypes.number }),
    chargebacks: PropTypes.shape({ count: PropTypes.number, amount: PropTypes.number }),
  }),
  payouts: PropTypes.shape({
    total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    count: PropTypes.number,
    weekly: PropTypes.arrayOf(PropTypes.object),
    recent: PropTypes.arrayOf(PropTypes.object),
  }),
  rebate: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string.isRequired,
    advance_amount: PropTypes.number.isRequired,
    amount_collected: PropTypes.number.isRequired,
    remaining: PropTypes.number.isRequired,
    progress_percent: PropTypes.number.isRequired,
    fully_recouped: PropTypes.bool.isRequired,
    per_ticket: PropTypes.string,
    collection_method: PropTypes.string,
    activated_on: PropTypes.string,
  }),
  previousRebates: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    advance_amount: PropTypes.number.isRequired,
    amount_collected: PropTypes.number.isRequired,
    progress_percent: PropTypes.number.isRequired,
    fully_recouped: PropTypes.bool.isRequired,
    activated_on: PropTypes.string,
  })),
  winRate: PropTypes.shape({
    won: PropTypes.number,
    decided: PropTypes.number,
    pct: PropTypes.number,
    benchmark: PropTypes.number,
  }),
};

FinanceOverview.defaultProps = {
  cards: {},
  payouts: null,
  rebate: null,
  previousRebates: null,
  winRate: null,
};

export default FinanceOverview;
